import { useCallback, useMemo } from "react";
import {
  type SidebarScope,
  fetchClientSidebarPage,
  selectRecentClients,
  selectSidebarQuery,
  selectSidebarSection,
} from "../slices/clientSlice";
import type { TClientForm } from "../types/index.type";
import { useAppDispatch, useAppSelector } from "./redux-hooks";

export type UseClientBrowse = {
  /** The browse list (empty-query dropdown content): same rows, same order as the sidebar. */
  browseClients: TClientForm[];
  browseLoading: boolean;
  browseHasMore: boolean;
  browseError: boolean;
  /** Fetch the first page if the shared list was never loaded — call on dropdown open. */
  ensureLoaded: () => void;
  /** Advance the shared cursor; the left-panel sidebar extends in lockstep. */
  loadMore: () => void;
  retryBrowse: () => void;
};

/**
 * The pickers' browse list, backed by THE sidebar's redux section for `scope` (`sidebar.all` /
 * `sidebar.shared`) — one source of truth, no per-picker copy. Scrolling a picker dropdown
 * paginates the very same section the left panel renders, so both stay in lockstep (a page loaded
 * by the sidebar is already in the picker, and vice versa).
 *
 * ``scope="all"`` mirrors the panel's "All Clients" tab (owned + shared, merged and sorted by the
 * server); ``scope="shared"`` mirrors the "Shared With Me" tab. Because the source IS the sidebar's
 * view, an active sidebar search filters the browse list too — that is the intended "duplicate of
 * the left panel" semantics, and `loadMore` continues with the sidebar's query so pages always
 * extend the list they belong to.
 *
 * On the demo path the sections are never fetched; the demo's stub clients (seeded via
 * `setRecentClients`) are served instead and fetching is disabled.
 */
export const useClientBrowse = (scope: SidebarScope = "all"): UseClientBrowse => {
  const dispatch = useAppDispatch();
  const { isDemoPath } = useAppSelector((state) => state.appState);
  const section = useAppSelector(selectSidebarSection(scope));
  const byId = useAppSelector((state) => state.client.byId);
  const sidebarQuery = useAppSelector(selectSidebarQuery);
  const demoClients = useAppSelector(selectRecentClients);

  const browseClients = useMemo(() => {
    if (isDemoPath) return demoClients;
    return section.ids.map((id) => byId[id]).filter(Boolean) as TClientForm[];
  }, [isDemoPath, demoClients, section.ids, byId]);

  const browseLoading = !isDemoPath && section.loading;
  const browseHasMore = !isDemoPath && section.hasMore;
  const browseError = !isDemoPath && section.error;

  const loadMore = useCallback(() => {
    if (isDemoPath || section.loading || !section.hasMore) return;
    dispatch(fetchClientSidebarPage({ scope, offset: section.offset, search: sidebarQuery, reset: false }));
  }, [isDemoPath, scope, section, sidebarQuery, dispatch]);

  const ensureLoaded = useCallback(() => {
    if (isDemoPath || section.loading || section.ids.length > 0) return;
    dispatch(fetchClientSidebarPage({ scope, offset: 0, search: sidebarQuery, reset: true }));
  }, [isDemoPath, scope, section, sidebarQuery, dispatch]);

  const retryBrowse = useCallback(() => {
    if (isDemoPath || !section.error) return;
    dispatch(
      fetchClientSidebarPage({ scope, offset: section.offset, search: sidebarQuery, reset: section.ids.length === 0 }),
    );
  }, [isDemoPath, scope, section, sidebarQuery, dispatch]);

  return { browseClients, browseLoading, browseHasMore, browseError, ensureLoaded, loadMore, retryBrowse };
};

export default useClientBrowse;
